/**
 * Calendar page — historical wellness journey.
 * Composition: Summary cards · View toggle (Año/Mes/Semana) · Active view
 * (with year navigation in YearView) + slide-from-right DayDetailDrawer.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import SummaryCards from "@/components/calendar/SummaryCards";
import ViewToggle from "@/components/calendar/ViewToggle";
import YearView from "@/components/calendar/YearView";
import MonthView from "@/components/calendar/MonthView";
import WeekView from "@/components/calendar/WeekView";
import DayDetailDrawer from "@/components/calendar/DayDetailDrawer";
import { parseISODate, todayLocalISO } from "@/lib/calendar";

const VIEW_OPTIONS = [
  { id: "year", label: "Año" },
  { id: "month", label: "Mes" },
  { id: "week", label: "Semana" },
];

export default function Calendar() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("year");
  const [focusDate, setFocusDate] = useState(() => new Date());
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  // Client-local "today" — pinned at mount so a single navigation can't
  // straddle midnight in the user's timezone.
  const todayISO = todayLocalISO();

  // Fetch overview when year changes (and on mount)
  useEffect(() => {
    let alive = true;
    api
      .get("/calendar/overview", { params: { year: selectedYear, today: todayISO } })
      .then(({ data }) => {
        if (!alive) return;
        setOverview(data);
        if (data?.today && selectedYear === parseISODate(data.today).getFullYear()) {
          setFocusDate(parseISODate(data.today));
        } else {
          setFocusDate(new Date(selectedYear, 11, 15));
        }
      })
      .catch(() => {
        if (!alive) return;
        toast.error("No pudimos cargar tu historial. Probá de nuevo.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [selectedYear, todayISO]);

  // Derived loading-on-year-switch: true while overview hasn't caught up
  // with the selected year.
  const switchingYear = !!overview && overview.year !== selectedYear;

  // Fetch day detail on selection
  useEffect(() => {
    if (!selectedDate) return;
    let alive = true;
    api
      .get(`/calendar/day/${selectedDate}`, { params: { today: todayISO } })
      .then(({ data }) => {
        if (alive) setDayDetail(data);
      })
      .catch(() => {
        if (!alive) return;
        toast.error("No pudimos cargar el detalle de ese día.");
      });
    return () => {
      alive = false;
    };
  }, [selectedDate, todayISO]);

  const dayLoading =
    Boolean(selectedDate) && (!dayDetail || dayDetail.date !== selectedDate);

  const days = overview?.days || [];
  const summary = overview?.summary;
  const availableYears = overview?.available_years || [selectedYear];
  const heatmapTodayISO = overview?.today || todayISO;

  const closeDrawer = () => setSelectedDate(null);
  const handleSelect = (iso) => setSelectedDate(iso);
  const handleEdit = (iso) => navigate(`/log?date=${iso}`);

  const handleViewChange = (nextView) => {
    if (selectedDate) {
      setFocusDate(parseISODate(selectedDate));
    }
    setView(nextView);
  };

  if (loading && !overview) {
    return (
      <div className="py-16 text-center text-ink-600" data-testid="calendar-loading">
        Cargando tu historial…
      </div>
    );
  }

  const isEmpty = !summary || summary.logs_count === 0;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="calendar-page">
      <header className="flex flex-col gap-2">
        <p className="tk-label">Tu jornada</p>
        <h1 className="font-outfit text-3xl text-ink-900">Calendario de bienestar</h1>
        <p className="font-dmsans text-sm text-ink-600 max-w-2xl">
          Cada día registrado se vuelve una pieza de tu jardín. Explorá tu evolución día a día,
          semana a semana, o el año completo de un solo vistazo.
        </p>
      </header>

      {isEmpty && selectedYear === new Date().getFullYear() ? (
        <EmptyState
          title="Tu jardín está esperando sus primeras semillas"
          description="Cuando hagas tu primer registro diario, tu historia empezará a florecer acá."
          action={
            <button
              type="button"
              onClick={() => navigate("/log")}
              className="tk-btn-primary"
              data-testid="calendar-cta-first-log"
            >
              Hacer mi primer registro
            </button>
          }
        />
      ) : (
        <>
          <SummaryCards summary={summary} />

          <div className="flex items-center justify-between flex-wrap gap-3">
            <ViewToggle
              options={VIEW_OPTIONS}
              value={view}
              onChange={handleViewChange}
              testId="calendar-view-toggle"
            />
            <p className="font-dmsans text-xs text-ink-600 hidden sm:block">
              Tocá cualquier día para ver el detalle
            </p>
          </div>

          <div data-testid="calendar-active-view" data-view={view}>
            <div
              className={`transition-all duration-500 ease-out ${
                Boolean(selectedDate) ? "lg:pr-[440px]" : ""
              }`}
            >
              {view === "year" && (
                <YearView
                  days={days}
                  selectedDate={selectedDate}
                  onSelect={handleSelect}
                  year={overview?.year || selectedYear}
                  availableYears={availableYears}
                  onYearChange={setSelectedYear}
                  todayISO={heatmapTodayISO}
                />
              )}
              {view === "month" && (
                <MonthView
                  focusDate={focusDate}
                  onChangeFocus={setFocusDate}
                  days={days}
                  selectedDate={selectedDate}
                  onSelect={handleSelect}
                />
              )}
              {view === "week" && (
                <WeekView
                  focusDate={focusDate}
                  onChangeFocus={setFocusDate}
                  days={days}
                  selectedDate={selectedDate}
                  onSelect={handleSelect}
                />
              )}
            </div>
          </div>
        </>
      )}

      <DayDetailDrawer
        open={Boolean(selectedDate)}
        loading={dayLoading}
        day={dayDetail}
        onClose={closeDrawer}
        onEdit={handleEdit}
      />
    </div>
  );
}
